import { useContext, useState } from "react";
import { Badge, Button } from "flowbite-react";
import { ResponseContext } from "../../context/responseProvider";

import { StockBadge } from "../../helpers/merch-manager/helper";
import { CustomLabel } from "../customLabel";
import { ImagePlaceholder } from "../icons";
import {
  approveRequest,
  declineRequest,
} from "../../services/merchManager/merchManagerActions";
import { FormInputClass } from "../FormInputClass";

const PendingRequestDetail = ({ request, merch, handleApi }) => {
  const [isApproving, setIsApproving] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [showDecline, setShowDecline] = useState(false);
  const [declineReason, setDeclineReason] = useState("");
  const [note, setNote] = useState("");
  const [reasonError, setReasonError] = useState("");
  const { setResponse, getResponse } = useContext(ResponseContext);

  const approveResponse = getResponse("approve_request");
  const declineResponse = getResponse("decline_request");

  const item = merch.find((m) => m.id === request.item_id);
  const badge = item ? StockBadge(item.item_count) : null;

  const handleApprove = () => {
    setIsApproving(true);
    approveRequest({
      itemId: request.item_id,
      requestId: request.id,
      note: note.trim(),
      approverEmail: localStorage.getItem("userEmail"),
      approverName:
        localStorage.getItem("userName") ?? localStorage.getItem("userEmail"),
      requesterEmail: request.requested_by,
      requesterName: request.requester_name,
      itemName: request.item_name,
      itemCount: request.item_count,
      purpose: request.purpose,
      setIsLoading: setIsApproving,
      setResponse,
      onSuccess: () => {
        handleApi();
      },
    });
  };

  const handleDecline = () => {
    if (!declineReason.trim()) {
      setReasonError("A reason is required when declining.");
      return;
    }
    setIsDeclining(true);
    declineRequest({
      requestId: request.id,
      itemId: request.item_id,
      requestedCount: request.item_count,
      reason: declineReason.trim(),
      note: note.trim(),
      declinerEmail: localStorage.getItem("userEmail"),
      declinerName:
        localStorage.getItem("userName") ?? localStorage.getItem("userEmail"),
      requesterEmail: request.requested_by,
      requesterName: request.requester_name,
      itemName: request.item_name,
      itemCount: request.item_count,
      purpose: request.purpose,
      setIsLoading: setIsDeclining,
      setResponse,
      onSuccess: () => {
        handleApi();
      },
    });
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
            {item?.item_name}
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">
            by {request.requester_name ?? request.requested_by}
          </p>
        </div>
        <div>
          <p className="text-[11px] text-gray-400 whitespace-nowrap">
            Requested{" "}
            <span className="text-gray-500 dark:text-gray-300">
              {item?.updatedAt
                ? item.updatedAt.toDate().toLocaleDateString("en-AU", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })
                : "-"}
            </span>
          </p>
        </div>
      </div>

      {/* ── Item preview ── */}
      <div className="flex gap-3 items-center rounded-xl bg-gray-50 dark:bg-gray-800 p-3">
        {item?.assets?.[0] ? (
          <img
            src={item.assets[0].url}
            alt={item.item_name}
            className="w-14 h-14 rounded-lg object-cover shrink-0"
          />
        ) : (
          <div className="w-14 h-14 rounded-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0">
            <ImagePlaceholder />
          </div>
        )}
        <div className="flex flex-col justify-center min-w-0 gap-1">
          <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">
            {request.item_name}
          </p>
          {item && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">
                {item.item_count} remaining in stock
              </span>
              <Badge color={badge.color} size="xs">
                {badge.label}
              </Badge>
            </div>
          )}
        </div>
      </div>

      {/* ── Request info ── */}
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2">
          <p className="text-xs text-gray-400 mb-1">Requested</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">
            {request.item_count}
            <span className="text-xs font-normal text-gray-400 ml-1">
              items
            </span>
          </p>
        </div>
        <div className="rounded-lg bg-gray-50 dark:bg-gray-800 px-3 py-2">
          <p className="text-xs text-gray-400 mb-1">Purpose</p>
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {request.purpose || (
              <span className="text-gray-400 font-normal">—</span>
            )}
          </p>
        </div>
      </div>

      <div>
        <CustomLabel text="Note (optional)" />
        <textarea
          rows={2}
          className={FormInputClass(false) + " resize-none"}
          placeholder="Add an internal note…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      {showDecline && (
        <div>
          <CustomLabel text="Decline reason" required />
          <textarea
            rows={2}
            className={FormInputClass(reasonError) + " resize-none"}
            placeholder="Explain why this request is being declined…"
            value={declineReason}
            onChange={(e) => {
              setDeclineReason(e.target.value);
              setReasonError("");
            }}
          />
          {reasonError && (
            <p className="text-xs text-red-400 mt-1">{reasonError}</p>
          )}
        </div>
      )}

      {approveResponse?.status === false && (
        <Badge color="failure">{approveResponse?.message}</Badge>
      )}
      {declineResponse?.status === false && (
        <Badge color="failure">{declineResponse?.message}</Badge>
      )}

      <div className="flex gap-2 mt-auto pt-2 border-t border-gray-100 dark:border-gray-700">
        {!showDecline ? (
          <>
            <button
              onClick={() => setShowDecline(true)}
              className="flex-1 px-4 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-500 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-950 transition-colors"
            >
              Decline
            </button>
            <Button
              onClick={handleApprove}
              disabled={isApproving}
              className="flex-1"
            >
              {isApproving ? "Approving..." : "Approve"}
            </Button>
          </>
        ) : (
          <>
            <button
              onClick={() => {
                setShowDecline(false);
                setDeclineReason("");
                setReasonError("");
              }}
              className="flex-1 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Back
            </button>
            <button
              onClick={handleDecline}
              disabled={isDeclining}
              className="flex-1 px-4 py-2 rounded-lg bg-red-500 text-white text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {isDeclining ? "Declining..." : "Confirm decline"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default PendingRequestDetail;
